import api from '../../services/api';
import { toast } from 'react-toastify';
import React, { useState } from 'react';
import { useAppSelector } from '../../store/hooks';
import { parseDataBRParaDate } from '../../services/utilitarios';
import { ehPlanoGratuito, estaEmPeriodoDeTeste, obterEhAdmin, obterIdTenent, obterIntegradoLocaSmartCheck } from '../../services/seguranca';
import FaturasGrid from './FaturasGrid';

interface AssinaturaTabProps {
    assinaturaData: {
        tempoRestante: string;
        proximaRenovacao: string;
        limiteVeiculos: number;
        planoAtual: string;
    };
}

const AssinaturaTab: React.FC<AssinaturaTabProps> = ({ assinaturaData }) => {
    const [token] = useState(useAppSelector(state => state.token));
    const usuarioAdm = obterEhAdmin(token);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [confirmationInput, setConfirmationInput] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [isSubmiting, setIsSubmiting] = useState(false);
    const [subtab, setSubtab] = useState<'plano' | 'faturas'>('plano');
    const integradoLocaSmartCheck = obterIntegradoLocaSmartCheck(token);
    const [isLocaSmartCheckModalOpen, setIsLocaSmartCheckModalOpen] = useState(false);
    const [locaSmartCheckSenha, setLocaSmartCheckSenha] = useState('');
    const [locaSmartCheckError, setLocaSmartCheckError] = useState<string | null>(null);
    const [isLocaSmartCheckSubmiting, setIsLocaSmartCheckSubmiting] = useState(false);

    const handleUpgradePlano = () => {
        window.location.href = '/planos';
    };

    function tratarErro(error: any) {
        const errorMessage = error.response?.data?.message || 'Erro desconhecido';
        toast.error(`Não foi possível continuar a operação: ${errorMessage}`);
        setError(errorMessage);
    }

    const handleCancelarPlano = async () => {
        if (confirmationInput.trim().toUpperCase() !== 'CANCELAR') {
            setError('Por favor, digite "CANCELAR" para confirmar.');
            return;
        }

        try {
            setIsSubmiting(true);
            await api.delete(`/api/payment/subscription/${obterIdTenent(token)}`)

            setIsModalOpen(false);
            setConfirmationInput('');
            setError(null);
            window.location.href = '/home';
            toast.success("Assinatura cancelada com sucesso");
            setIsSubmiting(false);
        } catch (err) {
            tratarErro(err);
            setIsSubmiting(false);
        }
    };

    const handleCancelarLocaSmartCheck = async () => {
        if (!locaSmartCheckSenha.trim()) {
            setLocaSmartCheckError('Informe sua senha para confirmar.');
            return;
        }
        try {
            setIsLocaSmartCheckSubmiting(true);
            await api.post('/LocaSmartCheck/Desativar', { senha: locaSmartCheckSenha });
            setIsLocaSmartCheckModalOpen(false);
            setLocaSmartCheckSenha('');
            toast.success("LocaSmartCheck desativado com sucesso. O valor foi removido da sua assinatura.");
            setTimeout(() => window.location.reload(), 1500);
        } catch (err: any) {
            setLocaSmartCheckError(err.response?.data?.message || err.response?.data?.Mensagem || 'Erro ao desativar LocaSmartCheck');
        } finally {
            setIsLocaSmartCheckSubmiting(false);
        }
    };

    const openModal = () => {
        setIsModalOpen(true);
        setConfirmationInput('');
        setError(null);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setConfirmationInput('');
        setError(null);
    };

    return (
        <div className="bg-white dark:bg-gray-900 rounded-lg">
            {/* Subtabs */}
            <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid #f1f5f9', marginBottom: 24 }}>
                <button
                    onClick={() => setSubtab('plano')}
                    style={{
                        padding: '12px 24px',
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        fontWeight: subtab === 'plano' ? 700 : 500,
                        color: subtab === 'plano' ? '#1e40af' : '#64748b',
                        borderBottom: subtab === 'plano' ? '2px solid #1e40af' : '2px solid transparent',
                        marginBottom: '-2px',
                        transition: 'all 0.15s',
                    }}
                >
                    Meu Plano
                </button>
                <button
                    onClick={() => setSubtab('faturas')}
                    style={{
                        padding: '12px 24px',
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        fontWeight: subtab === 'faturas' ? 700 : 500,
                        color: subtab === 'faturas' ? '#1e40af' : '#64748b',
                        borderBottom: subtab === 'faturas' ? '2px solid #1e40af' : '2px solid transparent',
                        marginBottom: '-2px',
                        transition: 'all 0.15s',
                    }}
                >
                    Faturas e Pagamentos
                </button>
            </div>

            {subtab === 'plano' && (
                <div className="p-6">
                    <h2 className="text-xl font-semibold text-black dark:text-gray-100 mb-4">Informações de Assinatura</h2>

                    {!usuarioAdm && (
                        <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-4">
                            <p className="font-bold">Atenção</p>
                            <p>Para alterar sua assinatura, entre em contato com nosso suporte.</p>
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl">
                        <div className="space-y-1">
                            <label className="block text-sm font-medium text-black dark:text-gray-200">
                                Plano Atual
                            </label>
                            <div className="p-3 bg-gray-100 dark:bg-gray rounded-md">
                                <p className="text-lg font-medium text-black dark:text-gray-100">
                                    {assinaturaData.planoAtual}
                                </p>
                            </div>
                        </div>

                        <div className="space-y-1">
                            <label className="block text-sm font-medium text-black dark:text-gray-200">
                                Limite de Veículos
                            </label>
                            <div className="p-3 bg-gray-100 dark:bg-gray rounded-md">
                                <p className="text-lg font-medium text-black dark:text-gray-100">
                                    {assinaturaData.limiteVeiculos} veículos
                                </p>
                            </div>
                        </div>

                        <div className="space-y-1">
                            <label className="block text-sm font-medium text-black dark:text-gray-200">
                                Tempo Restante
                            </label>
                            <div className="p-3 bg-gray-100 dark:bg-gray rounded-md">
                                <p className="text-lg font-medium text-black dark:text-gray-100">
                                    {assinaturaData.tempoRestante}
                                </p>
                            </div>
                        </div>

                        <div className="space-y-1">
                            <label className="block text-sm font-medium text-black dark:text-gray-200">
                                Próxima Renovação
                            </label>
                            <div className="p-3 bg-gray-100 dark:bg-gray rounded-md">
                                <p className="text-lg font-medium text-black dark:text-gray-100">
                                    {assinaturaData.proximaRenovacao}
                                </p>
                            </div>
                        </div>

                        <div className="space-y-1">
                            <label className="block text-sm font-medium text-black dark:text-gray-200">
                                Status
                            </label>
                            <div className="p-3 bg-gray-100 dark:bg-gray rounded-md">
                                <p className="text-lg font-medium text-black dark:text-gray-100">
                                    {parseDataBRParaDate(assinaturaData.proximaRenovacao) < new Date() ? 'EM ATRASO' : 'ATIVO'}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="pt-6 flex flex-row flex-wrap gap-4">
                        <button
                            onClick={handleUpgradePlano}
                            className="px-4 py-2 bg-[#1F467E] text-white rounded-md hover:bg-[#2a5a9e] transition-colors focus:outline-none focus:ring-2 focus:ring-[#1F467E] focus:ring-offset-2"
                        >
                            {(ehPlanoGratuito(token) || estaEmPeriodoDeTeste(token)) ? 'Ativar Plano' : 'Expandir Limite de Veículos'}
                        </button>

                        {!(estaEmPeriodoDeTeste(token)) && (
                            <button
                                onClick={openModal}
                                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2"
                            >
                                Cancelar Plano
                            </button>
                        )}
                        {integradoLocaSmartCheck && (
                            <button
                                onClick={() => { setIsLocaSmartCheckModalOpen(true); setLocaSmartCheckSenha(''); setLocaSmartCheckError(null); }}
                                className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors focus:outline-none focus:ring-2 focus:ring-orange-600 focus:ring-offset-2"
                            >
                                Cancelar LocaSmartCheck
                            </button>
                        )}
                    </div>
                </div>
            )}

            {subtab === 'faturas' && (
                <FaturasGrid />
            )}

            {isModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-lg max-w-md w-full">
                        <h3 className="text-lg font-semibold text-black dark:text-gray-100 mb-4">
                            Confirmar Cancelamento
                        </h3>
                        <p className="text-sm text-black dark:text-gray-200 mb-4">
                            Para confirmar o cancelamento da sua assinatura, digite <strong>CANCELAR</strong> no campo abaixo.
                        </p>
                        <input
                            type="text"
                            value={confirmationInput}
                            onChange={(e) => setConfirmationInput(e.target.value)}
                            className="w-full p-2 mb-4 border rounded-md text-black dark:text-gray-100 bg-gray dark:bg-gray border-gray dark:border-gray focus:outline-none focus:ring-2 focus:ring-[#1F467E]"
                            placeholder="Digite CANCELAR"
                        />
                        {error && (
                            <p className="text-red-600 text-sm mb-4">{error}</p>
                        )}
                        <div className="flex justify-end gap-4">
                            <button
                                onClick={closeModal}
                                className="px-4 py-2 bg-gray-300 text-black rounded-md hover:bg-gray-400 transition-colors"
                            >
                                Fechar
                            </button>
                            <button
                                disabled={isSubmiting}
                                onClick={handleCancelarPlano}
                                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                            >
                                Confirmar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isLocaSmartCheckModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-lg max-w-md w-full">
                        <h3 className="text-lg font-semibold text-black dark:text-gray-100 mb-4">
                            Cancelar LocaSmartCheck
                        </h3>
                        <p className="text-sm text-black dark:text-gray-200 mb-4">
                            Ao cancelar, o valor do LocaSmartCheck será removido da sua assinatura e o acesso às vistorias integradas será desativado.
                        </p>
                        <p className="text-sm text-black dark:text-gray-200 mb-2">
                            Digite sua senha para confirmar:
                        </p>
                        <input
                            type="password"
                            value={locaSmartCheckSenha}
                            onChange={(e) => setLocaSmartCheckSenha(e.target.value)}
                            className="w-full p-2 mb-4 border rounded-md text-black dark:text-gray-100 bg-gray dark:bg-gray border-gray dark:border-gray focus:outline-none focus:ring-2 focus:ring-[#1F467E]"
                            placeholder="Sua senha"
                        />
                        {locaSmartCheckError && (
                            <p className="text-red-600 text-sm mb-4">{locaSmartCheckError}</p>
                        )}
                        <div className="flex justify-end gap-4">
                            <button
                                onClick={() => { setIsLocaSmartCheckModalOpen(false); setLocaSmartCheckSenha(''); setLocaSmartCheckError(null); }}
                                className="px-4 py-2 bg-gray-300 text-black rounded-md hover:bg-gray-400 transition-colors"
                            >
                                Voltar
                            </button>
                            <button
                                disabled={isLocaSmartCheckSubmiting}
                                onClick={handleCancelarLocaSmartCheck}
                                className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors"
                            >
                                {isLocaSmartCheckSubmiting ? 'Cancelando...' : 'Confirmar Cancelamento'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AssinaturaTab;
